import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/auth.store';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { showSuccess, showError } from '@/lib/toast';
import { api } from '@/lib/api';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { User, Globe, Lock, Shield } from 'lucide-react';

export function Settings() {
  const { t, i18n } = useTranslation();
  const { user, updateUser } = useAuthStore();
  const [name, setName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch('/users/profile', { name, language: i18n.language });
      updateUser(data);
      showSuccess(t('common.success'));
    } catch (error: any) {
      showError(error.response?.data?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      showError(t('settings.passwordsDoNotMatch'));
      return;
    }
    if (newPassword.length < 8) {
      showError(t('settings.passwordMinLength'));
      return;
    }
    setChangingPassword(true);
    try {
      await api.patch('/users/password', { currentPassword, newPassword });
      showSuccess(t('settings.passwordChanged'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      showError(error.response?.data?.message || t('common.error'));
    } finally {
      setChangingPassword(false);
    }
  };

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  };

  return (
    <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{t('settings.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('settings.subtitle')}
          </p>
        </div>

        {/* Profile */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <User className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold">{t('common.profile')}</CardTitle>
                <CardDescription className="text-sm">{t('settings.profileDescription')}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <Separator />
          <CardContent className="pt-6 space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium">{t('auth.register.name')}</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-10"
                  placeholder="Enter your full name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">{t('auth.register.email')}</Label>
                <Input
                  id="email"
                  value={user?.email || ''}
                  disabled
                  className="bg-muted/50 h-10"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <Shield className="h-3.5 w-3.5" />
              {t('settings.emailCannotChange')}
            </p>
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t('common.billing')}</Label>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium px-3 py-1.5 rounded-md bg-emerald-500/10 text-emerald-600 border border-emerald-500/20">
                  {t('settings.perInstanceBilling')}
                </span>
              </div>
            </div>
            <div className="flex justify-end pt-2">
              <Button onClick={handleSave} disabled={saving} className="px-6">
                {saving ? t('common.loading') : t('common.save')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Password */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                <Lock className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold">{t('settings.password')}</CardTitle>
                <CardDescription className="text-sm">{t('settings.passwordDescription')}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <Separator />
          <CardContent className="pt-6 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="current-password" className="text-sm font-medium">{t('settings.currentPassword')}</Label>
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="h-10 max-w-md"
                placeholder="Enter current password"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="new-password" className="text-sm font-medium">{t('settings.newPassword')}</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="h-10"
                  placeholder="Min. 8 characters"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="text-sm font-medium">{t('settings.confirmNewPassword')}</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="h-10"
                  placeholder="Confirm password"
                />
              </div>
            </div>
            <div className="flex justify-end pt-2">
              <Button
                onClick={handleChangePassword}
                disabled={changingPassword || !currentPassword || !newPassword}
                variant="outline"
                className="px-6"
              >
                {changingPassword ? t('common.loading') : t('settings.changePassword')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Language */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Globe className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold">{t('settings.language')}</CardTitle>
                <CardDescription className="text-sm">{t('settings.languageDescription')}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <Separator />
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {[
                { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
                { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵' },
                { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
                { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
                { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷' },
                { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
                { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇧🇷' },
                { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
                { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦' },
              ].map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={cn(
                    'flex items-center gap-2.5 px-3 py-2.5 rounded-lg border transition-colors',
                    i18n.language === lang.code
                      ? 'border-emerald-500/50 bg-emerald-500/5 ring-1 ring-emerald-500/20'
                      : 'border-border hover:bg-muted/50',
                  )}
                >
                  <span className="text-xl">{lang.flag}</span>
                  <div className="text-left flex-1 min-w-0">
                    <p className="font-medium text-xs truncate">{lang.nativeName}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{lang.name}</p>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Account */}
        <Card className="border-red-200 dark:border-red-900/50">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <Shield className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold">{t('settings.account')}</CardTitle>
                <CardDescription className="text-sm">{t('settings.accountDescription')}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <Separator />
          <CardContent className="pt-6">
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/50 mb-4">
              <p className="text-sm text-red-600 dark:text-red-400">
                {t('settings.deleteAccountWarning')}
              </p>
            </div>
            <Button variant="destructive" className="px-6">
              {t('settings.deleteAccount')}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
